import * as React from 'react';
import { Image, Popconfirm, Spin, message } from 'antd';
import { CrownOutlined, EyeOutlined, SyncOutlined } from '@ant-design/icons';
import { TableColumn } from 'react-data-table-component';

import { Button, buttonType } from 'components/button/button';
import { Card } from 'components/card/card';
import { CopyablePhone } from 'components/copyable-phone/copyable-phone';
import { Input } from 'components/input/input';
import { Modal } from 'components/modal/modal';
import { Page } from 'components/page/page';
import { Table } from 'components/table/table';
import { UserLink } from 'components/user-link/user-link';
import { GoldUpgradeCandidate, GoldUpgradeCandidateDetails, PaginationList } from 'core/domain';
import {
    getGoldUpgradeCandidateDetails,
    getGoldUpgradeCandidates,
    upgradeUserToGold,
} from 'core/repositories/marketing';
import { digitSperator, onlyEnDigits } from 'core/utils/decimalExtentions';
import { faNum, formatDate, toQuery } from '../marketing-utils';
import '../marketing.scss';
import './gold-upgrade.scss';

const { useEffect, useState } = React;

// همان پیش‌فرض GetGoldUpgradeCandidatesQuery در بک‌اند
const DEFAULT_MIN_BUY = 200_000_000;

const toman = (value: number) => `${faNum(Math.round(value))} تومان`;

interface DetailsModalProps {
    candidate?: GoldUpgradeCandidate;
    setCandidate: React.Dispatch<React.SetStateAction<GoldUpgradeCandidate | undefined>>;
    onUpgraded: () => void;
}

/** تصاویر احراز کاربر؛ ادمین بعد از دیدنشان تصمیم به ارتقا می‌گیرد. */
const DetailsModal: React.FC<DetailsModalProps> = ({ candidate, setCandidate, onUpgraded }) => {
    const [details, setDetails] = useState<GoldUpgradeCandidateDetails>();
    const [loading, setLoading] = useState(false);
    const [upgrading, setUpgrading] = useState(false);

    useEffect(() => {
        setDetails(undefined);
        if (!candidate) return;
        setLoading(true);
        getGoldUpgradeCandidateDetails(candidate.userId).then((value) => {
            setLoading(false);
            if (value) setDetails(value);
        });
    }, [candidate]);

    const close = () => setCandidate(undefined);

    const upgrade = async () => {
        if (!candidate) return;
        setUpgrading(true);
        const result = await upgradeUserToGold(candidate.userId);
        setUpgrading(false);
        if (!result) return;
        if (result.smsSent) message.success('کاربر به سطح طلایی ارتقا یافت و پیامک برایش ارسال شد.');
        else message.warning('کاربر به سطح طلایی ارتقا یافت، اما پیامک ارسال نشد (در اکتیویتی‌ها ثبت شد).');
        close();
        onUpgraded();
    };

    return (
        <Modal
            isOpen={!!candidate}
            setIsOpen={(open) => !open && close()}
            className="mk-modal mk-modal--wide gold-upgrade-modal"
        >
            <h4>مدارک احراز هویت</h4>
            {candidate && (
                <div className="gold-upgrade-summary">
                    <div>
                        <span>کاربر</span>
                        <b>{candidate.fullName?.trim() || 'بدون نام'}</b>
                    </div>
                    <div>
                        <span>شماره تماس</span>
                        <CopyablePhone phoneNumber={candidate.phoneNumber} />
                    </div>
                    <div>
                        <span>کد ملی</span>
                        <b>{details?.nationalCode ?? '-'}</b>
                    </div>
                    <div>
                        <span>سطح فعلی</span>
                        <b>{candidate.levelName ?? '-'}</b>
                    </div>
                    <div>
                        <span>مجموع خرید</span>
                        <b>{toman(candidate.totalBuyAmount)}</b>
                    </div>
                    <div>
                        <span>تعداد خرید</span>
                        <b>{faNum(candidate.buyCount)}</b>
                    </div>
                </div>
            )}
            <Spin spinning={loading}>
                {details?.images?.length ? (
                    <div className="gold-upgrade-images">
                        <Image.PreviewGroup>
                            {details.images.map((image, index) => (
                                <figure key={index}>
                                    <Image src={`data:${image.contentType || 'image/jpeg'};base64,${image.content}`} />
                                    <figcaption>{formatDate(image.createdOn)}</figcaption>
                                </figure>
                            ))}
                        </Image.PreviewGroup>
                    </div>
                ) : (
                    <p className="mk-empty">{loading ? 'در حال بارگذاری تصاویر...' : 'تصویر احرازی برای این کاربر ثبت نشده است.'}</p>
                )}
            </Spin>
            <div className="gold-upgrade-actions">
                <Popconfirm
                    title="ارتقا به سطح طلایی"
                    description="سطح کاربر طلایی می‌شود و پیامک ارتقا برایش ارسال می‌شود. ادامه می‌دهید؟"
                    okText="بله، ارتقا بده"
                    cancelText="انصراف"
                    onConfirm={upgrade}
                    disabled={loading || upgrading}
                >
                    <Button
                        type={buttonType.success}
                        text="ارتقا به طلایی"
                        icon={<CrownOutlined />}
                        loading={upgrading}
                        disabled={loading || upgrading}
                    />
                </Popconfirm>
                <Button type={buttonType.outlined} text="بستن" disabled={upgrading} onClick={close} />
            </div>
        </Modal>
    );
};

/** گزارش کاربرانی که مجموع خریدشان بالای سقف است و هنوز طلایی نیستند. */
export const GoldUpgradeCandidates: React.FC = () => {
    const [keyword, setKeyword] = useState('');
    const [minBuy, setMinBuy] = useState(String(DEFAULT_MIN_BUY));
    const [query, setQuery] = useState('');
    const [data, setData] = useState<PaginationList<GoldUpgradeCandidate>>();
    const [loading, setLoading] = useState(false);
    const [pageSize, setPageSize] = useState(10);
    const [selected, setSelected] = useState<GoldUpgradeCandidate>();

    const load = (page = 1) => {
        const nextQuery = toQuery({ keyword: keyword.trim(), minBuyAmount: Number(minBuy) || DEFAULT_MIN_BUY });
        setQuery(nextQuery);
        getGoldUpgradeCandidates(setLoading, setData, page, pageSize, nextQuery);
    };

    const reloadCurrentPage = () =>
        getGoldUpgradeCandidates(setLoading, setData, data?.pagination?.pageIndex || 1, pageSize, query);

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const columns: TableColumn<GoldUpgradeCandidate>[] = [
        {
            name: 'نام و نام خانوادگی',
            id: 'user',
            selector: (row) => row.fullName ?? '',
            cell: (row) => (
                <div className="mk-user-cell">
                    <UserLink userId={row.userId}>{row.fullName?.trim() || 'بدون نام'}</UserLink>
                    <small>{row.levelName}</small>
                </div>
            ),
            minWidth: '170px',
        },
        {
            name: 'شماره تماس',
            id: 'phoneNumber',
            selector: (row) => row.phoneNumber ?? '',
            cell: (row) => <CopyablePhone phoneNumber={row.phoneNumber} />,
            minWidth: '140px',
        },
        {
            name: 'تاریخ عضویت',
            id: 'registeredOn',
            selector: (row) => row.registeredOn,
            cell: (row) => formatDate(row.registeredOn),
            minWidth: '140px',
        },
        {
            name: 'تاریخ تایید مدرک',
            id: 'documentApprovedOn',
            selector: (row) => row.documentApprovedOn ?? '',
            cell: (row) =>
                row.documentApprovedOn ? formatDate(row.documentApprovedOn) : <span className="mk-muted">احراز خودکار</span>,
            minWidth: '140px',
        },
        {
            name: 'مجموع خرید',
            id: 'totalBuyAmount',
            selector: (row) => row.totalBuyAmount,
            cell: (row) => toman(row.totalBuyAmount),
            minWidth: '160px',
        },
        {
            name: 'تعداد خرید',
            id: 'buyCount',
            selector: (row) => row.buyCount,
            cell: (row) => faNum(row.buyCount),
            width: '100px',
        },
        {
            name: '',
            id: 'action',
            width: '130px',
            cell: (row) => (
                <div className="mk-start-call">
                    <Button type={buttonType.info} text="جزییات" icon={<EyeOutlined />} onClick={() => setSelected(row)} />
                </div>
            ),
        },
    ];

    return (
        <Page className="pmx-marketing" title="پتانسیل ارتقا به طلایی">
            <Card>
                <DetailsModal candidate={selected} setCandidate={setSelected} onUpgraded={reloadCurrentPage} />
                <div className="mk-panel">
                    <div className="mk-panel__toolbar">
                        <p>
                            کاربرانی که هنوز در سطح طلایی نیستند و مجموع خریدشان (سفارش‌های پرداخت‌شده) از مبلغ زیر
                            بیشتر است. با «جزییات» مدارک کاربر را ببینید و در صورت تایید او را به طلایی ارتقا دهید.
                        </p>
                    </div>
                    <div className="mk-panel__filters">
                        <Input
                            value={keyword}
                            placeholder="شماره موبایل یا نام کاربر"
                            onPressEnter={() => load()}
                            onChange={(e) => setKeyword(e.target.value)}
                        />
                        <Input
                            value={digitSperator(minBuy)}
                            inputMode="numeric"
                            placeholder="حداقل مجموع خرید (تومان)"
                            onPressEnter={() => load()}
                            onChange={(e) => setMinBuy(onlyEnDigits(e.target.value))}
                        />
                        <div className="mk-filter-buttons">
                            <Button type={buttonType.success} text="جستجو" disabled={loading} onClick={() => load()} />
                            <Button
                                type={buttonType.secondary}
                                text="بروزرسانی"
                                icon={<SyncOutlined />}
                                disabled={loading}
                                onClick={reloadCurrentPage}
                            />
                        </div>
                    </div>
                    <Spin spinning={loading}>
                        {data?.items?.length ? (
                            <Table
                                blur={loading}
                                data={data.items}
                                column={columns}
                                pagination={data.pagination}
                                pageSize={pageSize}
                                setPageSize={setPageSize}
                                setloader={setLoading}
                                setdata={setData}
                                getdata={getGoldUpgradeCandidates}
                                filters={query}
                            />
                        ) : (
                            <p className="mk-empty">
                                {loading
                                    ? 'در حال بارگذاری...'
                                    : data
                                      ? 'کاربری با این شرایط یافت نشد.'
                                      : 'بارگذاری فهرست انجام نشد. دوباره تلاش کنید.'}
                            </p>
                        )}
                    </Spin>
                    {!!data?.pagination?.totalCount && (
                        <p className="mk-muted">{faNum(data.pagination.totalCount)} کاربر با پتانسیل ارتقا</p>
                    )}
                </div>
            </Card>
        </Page>
    );
};
